import axios, {
    AxiosError,
    CanceledError,
    type AxiosInstance,
    type AxiosResponse,
    type CreateAxiosDefaults,
    type InternalAxiosRequestConfig,
} from 'axios';
import type { AuthRuntime } from '../auth/index';
import {
    appendTraceId,
    errorReason,
    isHtmlPage,
    readHeader,
    readTraceId,
    unknownFailureText,
    withTraceId,
} from '../errors/index';
import { failureBodyLines } from './failureBody';
import {
    createMaintenanceRedirect,
    isServiceMaintenanceResponse,
    type IMaintenanceEnvironment,
    type IMaintenanceRedirectOptions,
} from './maintenance';

/** Что клиент берёт у сессии (`@srm/core/auth`) — только тип: auth о http не знает. В тестах — `fakeAuthPort()`. */
export type AuthPort = Pick<
    AuthRuntime,
    'authHeaders' | 'recoverFromUnauthorized' | 'expireRejectedAccess' | 'reportApiUnavailable'
> &
    Partial<Pick<AuthRuntime, 'pageUserChanged'>>;

/** Общий тост отказа — подмножество `INotification` `@srm/ui`: типов ui ядро не знает. */
export interface HttpNotification {
    message: string;
    variant: 'red' | 'yellow';
    traceId?: string;
}

/** Запрос глазами плагина — до отправки, и для axios, и для keepalive. */
export interface ApiRequestContext {
    /** Адрес как в `config.url` (keepalive — `path`): от `baseURL` или абсолютный. */
    readonly url: string | undefined;
    readonly baseURL: string;
    /** Заголовки, которые уйдут с запросом: плагин дописывает свои (`Authorization` клиент ставит после плагинов). */
    readonly headers: Record<string, string>;
    /**
     * axios — конфиг запроса: поля приложения (augmentation `AxiosRequestConfig`) и место для своей отметки (начало
     * запроса для журнала); keepalive — нет.
     */
    readonly config?: InternalAxiosRequestConfig;
}

/** Попытка запроса axios завершилась — каждая, в том числе повтор после ротации и `retry`. */
export interface ApiSettledRequest {
    readonly config: InternalAxiosRequestConfig | undefined;
    /** Ответ сервера; нет — сеть, тайм-аут или отмена. */
    readonly response: AxiosResponse | undefined;
    readonly outcome: 'success' | 'error' | 'canceled';
}

/**
 * Отказ, который клиент отдаёт вызывающему коду (общий тост, разобранное тело, текст неизвестного отказа). Не
 * отказ: отмена, 401 (решение сессии, и на повторе после ротации), переход на обслуживание, промежуточные попытки
 * `retry`, проброс `passThroughStatuses`.
 */
export interface ApiFailure {
    readonly error: AxiosError;
    readonly config: InternalAxiosRequestConfig | undefined;
    /** Код ответа; нет — без ответа (сеть, тайм-аут). */
    readonly status: number | undefined;
    /** Код обращения — из заголовка или тела (Blob уже развёрнут). */
    readonly traceId: string | undefined;
}

/**
 * Плагин клиента. Порядок плагинов задаёт приложение: `request` идут по порядку до первого отказа, наблюдатели
 * (`settled`, `response`, `failure`) — все по порядку; исключение наблюдателя запрос не ломает.
 */
export interface ApiPlugin {
    readonly name: string;
    /** До отправки — и для axios, и для keepalive. Строка — отменить: `CanceledError` с ней (axios) / `false` (keepalive). */
    request?(ctx: ApiRequestContext): void | string;
    /** Ответ сервера — и успех, и отказ с ответом; до разбора отказа. */
    response?(response: AxiosResponse, outcome: 'success' | 'error'): void;
    /** Итог попытки — и без ответа (сеть, отмена): журналы запросов. До `response`. */
    settled?(request: ApiSettledRequest): void;
    /** Отказ для вызывающего кода (см. `ApiFailure`) — до общего тоста. */
    failure?(failure: ApiFailure): void;
}

/**
 * Швы окружения для тестов (`fetch` — и статус стенда, и keepalive); приложение не передаёт. Умолчания — глобальные
 * объекты в момент вызова.
 */
export type ApiClientEnvironment = IMaintenanceEnvironment;

export interface ApiClientOptions {
    /** База API — `config.application.apiUrl`. */
    baseURL: string;
    withCredentials: boolean;
    auth: AuthPort;
    /** Общий тост отказа (`sendNotification` `@srm/ui`). */
    notify(notification: HttpNotification): void;
    /** Показывать ли общий тост отказа; по умолчанию — всегда. */
    shouldNotifyFailure?(error: AxiosError): boolean;
    /** Статусы, которые клиент всегда пропускает исходной AxiosError; к ним добавляется `config.passThroughStatuses`. */
    passThroughStatuses?: readonly number[];
    /** Сериализация `params`; нет — умолчание axios. */
    paramsSerializer?: CreateAxiosDefaults['paramsSerializer'];
    /** 503 с `code: SERVICE_MAINTENANCE` → страница обслуживания (`createMaintenanceRedirect`); `false` — без перехода. */
    maintenance: IMaintenanceRedirectOptions | false;
    plugins?: readonly ApiPlugin[];
    env?: ApiClientEnvironment;
}

export interface ApiClient {
    /** Клиент приложения (`$api`): Bearer, ротация на 401, плагины, разбор отказа. */
    readonly http: AxiosInstance;
    /** База API (`API_URL`). */
    readonly baseURL: string;
    /**
     * Запрос при уходе со страницы (pagehide) мимо axios: обычный вкладка оборвёт, keepalive — доживёт. Те же
     * `request`-плагины и Bearer; ответа никто не ждёт. `path` — от `baseURL`. `payload` — JSON-сериализуемое значение
     * либо готовое тело (`Uint8Array`, например сжатый JSON — `Content-Encoding` передаёт вызывающий в `headers`).
     * `false` — не отправлен: тело больше `KEEPALIVE_LIMIT_BYTES` или запрос отменил плагин.
     */
    readonly sendKeepalive: (
        method: 'PUT' | 'POST',
        path: string,
        payload: unknown,
        headers?: Record<string, string>,
    ) => boolean;
}

/** Причина отмены запроса страницы, которая ещё показывает прежнего пользователя (`AuthRuntime.pageUserChanged`). */
const PAGE_USER_CHANGED = 'page-user-changed';

/** Потолок тела keepalive-запроса в браузерах (64 КиБ на все висящие запросы вкладки). */
export const KEEPALIVE_LIMIT_BYTES = 60 * 1024;

/** Стенд лежит (обновление, рестарт): nginx отвечает этими кодами. */
const UNAVAILABLE_STATUSES: readonly number[] = [502, 503, 504];
const DEFAULT_RETRY_DELAY_MS = 1000;

/** 401 запроса, а ротация временно не удалась (сеть, 5xx): вход цел, причина — сервер, а не авторизация. */
const REFRESH_UNAVAILABLE_TEXT = 'Сервер не ответил на продление входа. Повторите попытку позже.';

/**
 * Access-токен, с которым ушёл запрос, — его отклонил сервер. Запрос без токена (bootstrap профиля, вход по заголовку)
 * не доказывает, что текущий access плох: тогда undefined, и ротация проверяет свежесть вместо принудительной.
 */
const rejectedAccessToken = (config: InternalAxiosRequestConfig): string | undefined => {
    const header = readHeader(config.headers, 'authorization');
    return header?.startsWith('Bearer ') ? header.slice('Bearer '.length) : undefined;
};

/** Тело отказа скачивания (`responseType: 'blob'`) — JSON или текст; пустое и страница прокси причиной не считаются. */
const unwrapBlob = async (blob: Blob): Promise<unknown> => {
    let text: string;
    try {
        text = await blob.text();
    } catch {
        return blob;
    }
    try {
        return JSON.parse(text);
    } catch {
        return text.trim() && !isHtmlPage(text) ? text : blob;
    }
};

const toastText = (status: number | undefined) =>
    status
        ? `Не удалось выполнить запрос (код ${status}). Попробуйте снова.`
        : 'Не удалось выполнить запрос. Проверьте соединение и попробуйте снова.';

/**
 * HTTP-клиент приложения. Создание ничего не делает (ни запросов, ни подписок); экземпляр держит модуль приложения
 * (`@/http`). Отказ для вызывающего кода — прежней формы: `string[]` с неперечислимым `traceId` (разобранное тело
 * 400–599), `string` (неизвестный отказ «Ошибка получения данных с сервера (код N)», временный отказ ротации —
 * `REFRESH_UNAVAILABLE_TEXT`), исходная AxiosError (отмена, 401 после неудачной ротации и на повторе после удачной,
 * `passThroughStatuses`, обслуживание).
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
    const { baseURL, withCredentials, auth, plugins = [], env = {} } = options;
    const passThroughStatuses = options.passThroughStatuses ?? [];
    const redirectToMaintenance = options.maintenance ? createMaintenanceRedirect(options.maintenance, env) : null;

    const http = axios.create({
        baseURL,
        withCredentials,
        ...(options.paramsSerializer ? { paramsSerializer: options.paramsSerializer } : {}),
    });

    /** `request`-плагины по порядку; строка первого отказавшего — причина отмены. */
    const runRequestPlugins = (ctx: ApiRequestContext): string | undefined => {
        for (const plugin of plugins) {
            const blocked = plugin.request?.(ctx);
            if (typeof blocked === 'string') return blocked;
        }
        return undefined;
    };

    const observe = (call: (plugin: ApiPlugin) => void) => {
        for (const plugin of plugins) {
            try {
                call(plugin);
            } catch (error) {
                console.error(`HTTP-клиент: плагин «${plugin.name}» упал`, error);
            }
        }
    };

    http.interceptors.request.use((config) => {
        const ctx: ApiRequestContext = {
            url: config.url,
            baseURL: config.baseURL ?? baseURL,
            headers: {},
            config,
        };
        // Страница прежнего пользователя после переключения учётной записи: её запрос не уходит ни под чужим токеном,
        // ни без него (401 → ротация уже чужого входа).
        if (!config.skipBearerAuth && auth.pageUserChanged?.()) throw new CanceledError(PAGE_USER_CHANGED, config);
        const blocked = runRequestPlugins(ctx);
        if (blocked !== undefined) throw new CanceledError(blocked, config);

        config.headers.set(ctx.headers);
        // Вход по заголовку (SSO через Negotiate прокси): `Authorization` занят рукопожатием браузера.
        // CSRF здесь не нужен: RefreshToken и SignOut уходят транспортом сессии (`auth`).
        if (!config.skipBearerAuth) config.headers.set(auth.authHeaders());
        return config;
    });

    const onFailure = async (error: unknown): Promise<unknown> => {
        // Не отказ запроса (ошибка кода до отправки) — как есть: ни флага «стенд недоступен», ни тоста.
        if (!axios.isAxiosError(error)) throw error;

        // Конфига может не быть (ошибка интерцептора приложения) — отказ всё равно разбирается.
        const config = error.config;
        const response = error.response;
        const status = response?.status;
        const canceled = axios.isCancel(error);

        observe((plugin) => plugin.settled?.({ config, response, outcome: canceled ? 'canceled' : 'error' }));
        if (response) observe((plugin) => plugin.response?.(response, 'error'));

        if (status === 503 && redirectToMaintenance && (await isServiceMaintenanceResponse(response?.data))) {
            redirectToMaintenance();
            throw error;
        }

        if (config?.retry && !canceled) {
            config.retry -= 1;
            await new Promise<void>((resolve) => setTimeout(resolve, config.retryDelay || DEFAULT_RETRY_DELAY_MS));
            return http.request(config);
        }

        if (status === 401 && config && !config._isRetry) {
            // Ротация, гонка с соседней вкладкой и выход (если нужен) — решение сессии.
            const decision = await auth.recoverFromUnauthorized(rejectedAccessToken(config));
            if (decision === 'retry') {
                config._isRetry = true;
                return http.request(config);
            }
            if (decision === 'reject') throw error;
            if (!config.suppressErrorNotification && (options.shouldNotifyFailure?.(error) ?? true))
                options.notify({ message: REFRESH_UNAVAILABLE_TEXT, variant: 'red' });
            // eslint-disable-next-line @typescript-eslint/only-throw-error -- прежняя форма отказа: строка
            throw REFRESH_UNAVAILABLE_TEXT;
        }

        // Повтор после ротации снова 401 со свежим токеном: сервер сессию не принимает — выход решает сессия. Запрос
        // без токена (вход по заголовку) о сессии не говорит — обычный отказ.
        const retriedAccess = status === 401 && config?._isRetry ? rejectedAccessToken(config) : undefined;
        if (retriedAccess) {
            auth.expireRejectedAccess(retriedAccess);
            throw error;
        }

        if (canceled) throw error;

        if (
            status !== undefined &&
            (passThroughStatuses.includes(status) || config?.passThroughStatuses?.includes(status))
        )
            throw error;

        // Стенд лежит либо сеть недоступна: страница входа уходит в режим обслуживания (флаг — у приложения).
        if (status === undefined || UNAVAILABLE_STATUSES.includes(status)) auth.reportApiUnavailable();

        // Код обращения бэка: пользователь называет его в поддержку, поддержка ищет по нему запись в журнале. Есть на
        // любом ответе приложения, но не на отказах nginx и сети; у скачивания — и в теле, развёрнутом ниже.
        let traceId = readTraceId(error);
        if (response?.data instanceof Blob) {
            response.data = await unwrapBlob(response.data);
            traceId ??= readTraceId(error);
        }

        observe((plugin) => plugin.failure?.({ error, config, status, traceId }));

        const toast = toastText(status);
        if (!config?.suppressErrorNotification && (options.shouldNotifyFailure?.(error) ?? true))
            options.notify({ message: toast, variant: 'red', traceId });

        if (response && response.data !== undefined && status !== undefined && status >= 400 && status < 600) {
            const lines = failureBodyLines(response.data, toast);
            if (lines) {
                const failure = withTraceId(lines, traceId);
                // Тело причины не назвало (пустое, только фраза статуса — `title` ProblemDetails по умолчанию): код
                // ответа — канону, он покажет «(код N)». Неперечислимо, как `traceId`: форма прежняя.
                if (errorReason(failure) === undefined)
                    Object.defineProperty(failure, 'status', { value: status, enumerable: false, configurable: true });
                // eslint-disable-next-line @typescript-eslint/only-throw-error -- прежняя форма отказа: массив строк
                throw failure;
            }
        }

        console.error(error);
        // Форма — строка: модалки подписи различают этот отказ через typeof, код обращения — в тексте.
        const text = unknownFailureText(status);
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- прежняя форма отказа: строка
        throw traceId ? appendTraceId(`${text}.`, traceId, ' ') : text;
    };

    http.interceptors.response.use((response) => {
        observe((plugin) => plugin.settled?.({ config: response.config, response, outcome: 'success' }));
        observe((plugin) => plugin.response?.(response, 'success'));
        return response;
    }, onFailure);

    const sendKeepalive: ApiClient['sendKeepalive'] = (method, path, payload, headers = {}) => {
        if (auth.pageUserChanged?.()) return false;
        const ctx: ApiRequestContext = { url: path, baseURL, headers: {} };
        if (runRequestPlugins(ctx) !== undefined) return false;

        const body = payload instanceof Uint8Array ? payload : JSON.stringify(payload);
        const size = typeof body === 'string' ? new Blob([body]).size : body.byteLength;
        if (size > KEEPALIVE_LIMIT_BYTES) return false;

        void (env.fetch ?? fetch)(`${baseURL}${path}`, {
            method,
            // slice — копия на ArrayBuffer: BodyInit не принимает вид, который может лежать на SharedArrayBuffer
            body: typeof body === 'string' ? body : body.slice(),
            headers: { 'Content-Type': 'application/json', ...ctx.headers, ...headers, ...auth.authHeaders() },
            keepalive: true,
            credentials: withCredentials ? 'include' : 'same-origin',
        }).catch(() => undefined);
        return true;
    };

    return { http, baseURL, sendKeepalive };
}
